import { Button } from '@/components/ui/button'
import { Check, PartyPopper, ArrowRight } from 'lucide-react'

interface CompletedUnit {
  id: string
  task: string
}

interface ChapterCompleteCardProps {
  chapterNumber: number
  completedUnits: CompletedUnit[]
  onStartCheckpoint: () => void
}

export function ChapterCompleteCard({
  chapterNumber,
  completedUnits,
  onStartCheckpoint,
}: ChapterCompleteCardProps) {
  return (
    <div className="relative">
      {/* Decorative background elements - celebratory colors */}
      <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-emerald-200/40 to-teal-200/40 rounded-full blur-3xl" />
      <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br from-teal-200/30 to-cyan-200/30 rounded-full blur-3xl" />

      {/* Main card */}
      <div className="relative bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/80 rounded-3xl border border-emerald-200/50 shadow-xl overflow-hidden">
        {/* Top decorative bar - emerald gradient for celebration */}
        <div className="h-2 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400" />

        <div className="p-6 md:p-8">
          {/* Celebration header */}
          <div className="text-center mb-8 animate-bounce-in">
            {/* Confetti icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-teal-400 shadow-lg shadow-emerald-200/50 mb-4">
              <PartyPopper className="w-8 h-8 text-white" />
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              Chapter {chapterNumber} Complete!
            </h2>

            <p className="text-gray-600">
              You finished all {completedUnits.length} units! Amazing work.
            </p>
          </div>

          {/* Divider */}
          <div className="border-t border-emerald-200/50 mb-6" />

          {/* Completed units list */}
          <div className="mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-4">
              What You Learned
            </h3>

            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-emerald-100/50">
              <ul className="space-y-3">
                {completedUnits.map((unit, index) => (
                  <li
                    key={unit.id}
                    className="flex items-start gap-3 animate-fade-in"
                    style={{ animationDelay: `${0.3 + index * 0.05}s` }}
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-gray-700">{unit.task}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-emerald-200/50 mb-6" />

          {/* CTA section */}
          <div
            className="text-center animate-fade-in"
            style={{ animationDelay: '0.5s' }}
          >
            <p className="text-sm text-gray-600 mb-4">
              Let's reflect on your progress and plan your next chapter!
            </p>

            <Button
              size="lg"
              onClick={onStartCheckpoint}
              className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-300 hover:scale-[1.02] rounded-xl"
            >
              <PartyPopper className="w-5 h-5 mr-2" />
              Let's Reflect & Plan Next
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            {/* Time estimate badge */}
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100/60 text-emerald-800 text-sm">
              <span>Quick 5-minute checkpoint session</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
